import { useState } from 'react';

import { usePostContentContext } from '@/postContentElement/context';
import { buildYouTubeThumbnailSrcSet, buildYouTubeThumbnailUrl } from '@/rssFeedElement/util';
import { YoutubeLogoSVG } from '@/rssFeedElement/YouTubeLogoSVG';
import { evaluateAndAppendUTMParams } from '@/utils/evaluateAndAppendUTMParams';

import * as styles from '../ServiceEmbed.css';

const YOUTUBE_EMBED_VIDEO_ID_PATTERN = /\/embed\/([\w-]+)/;
const YOUTUBE_ORIGIN = 'https://www.youtube.com';
const YOUTUBE_PRIVACY_ORIGIN = 'https://www.youtube-nocookie.com';
const YOUTUBE_THUMBNAIL_BASE_URL = 'https://i.ytimg.com/vi';
const YOUTUBE_WATCH_URL = `${YOUTUBE_ORIGIN}/watch`;
const THUMBNAIL_SIZES = '(max-width: 520px) 100vw, 520px';
const PLAY_ICON_SIZE_PX = 64;

type YoutubeProps = {
  anchorId?: string;
  embedUrl?: string;
};

const parseEmbedUrl = (embedUrl: string) => {
  try {
    return new URL(embedUrl, YOUTUBE_ORIGIN);
  } catch {
    return undefined;
  }
};

// Module scope keeps the ref stable, so a re-render does not pull focus back to the player.
const focusOnMount = (iframe: HTMLIFrameElement | null) => iframe?.focus();

const Youtube = ({ anchorId, embedUrl }: YoutubeProps) => {
  const { utmParams } = usePostContentContext();
  const [isPlayerActive, setIsPlayerActive] = useState(false);
  const parsedEmbedUrl = embedUrl ? parseEmbedUrl(embedUrl) : undefined;
  const videoId = parsedEmbedUrl?.pathname.match(YOUTUBE_EMBED_VIDEO_ID_PATTERN)?.[1];

  if (!parsedEmbedUrl || !videoId) return null;

  // UTM params read document.location, so the player src is built only after a client-side click to keep SSR markup stable.
  const buildPlayerUrl = () => {
    const playerOrigin = parsedEmbedUrl.origin === YOUTUBE_PRIVACY_ORIGIN ? YOUTUBE_PRIVACY_ORIGIN : YOUTUBE_ORIGIN;
    const youtubeEmbedUrl = new URL(`/embed/${videoId}`, playerOrigin);
    youtubeEmbedUrl.search = parsedEmbedUrl.search;
    const playerUrl = new URL(evaluateAndAppendUTMParams(youtubeEmbedUrl.toString(), utmParams));
    playerUrl.searchParams.set('autoplay', '1');
    return playerUrl.toString();
  };

  const thumbnailBaseUrl = `${YOUTUBE_THUMBNAIL_BASE_URL}/${videoId}`;
  const watchUrl = new URL(YOUTUBE_WATCH_URL);
  watchUrl.searchParams.set('v', videoId);

  return (
    <div className="p-4">
      <div className="mx-auto" style={{ maxWidth: '520px' }} id={anchorId}>
        <div className="relative w-full min-w-[200px] min-h-[200px]" style={{ aspectRatio: '16 / 9' }}>
          {isPlayerActive ? (
            <iframe
              className="absolute top-0 left-0"
              width="100%"
              height="100%"
              ref={focusOnMount}
              src={buildPlayerUrl()}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              title="YouTube video"
            />
          ) : (
            <button
              type="button"
              aria-label="Play video"
              onClick={() => setIsPlayerActive(true)}
              className={styles.youtubeFacade}
            >
              <img
                src={buildYouTubeThumbnailUrl(thumbnailBaseUrl, 'hqdefault')}
                srcSet={buildYouTubeThumbnailSrcSet(thumbnailBaseUrl)}
                sizes={THUMBNAIL_SIZES}
                alt=""
                loading="lazy"
                className={styles.youtubeThumbnail}
              />
              <span aria-hidden="true" className={styles.youtubePlayIcon}>
                <YoutubeLogoSVG width={PLAY_ICON_SIZE_PX} height={PLAY_ICON_SIZE_PX} />
              </span>
            </button>
          )}
        </div>
        <noscript>
          <a href={watchUrl.toString()}>Watch on YouTube</a>
        </noscript>
      </div>
    </div>
  );
};

export { Youtube };
